import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './Home.css';
import QRCode from 'qrcode';

export default function Home() {
  const [eventCode, setEventCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [qrSrc, setQrSrc] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('eventCode');
    if (code) setEventCode(code);
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const target = `${window.location.origin}/`;

    QRCode.toDataURL(target, {
      errorCorrectionLevel: 'H',
      type: 'image/png',
      margin: 1,
      scale: 8,
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then((url) => setQrSrc(url))
      .catch((err) => {
        console.error('QR generation error', err);
        setQrSrc('');
      });
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      if (e && e.preventDefault) e.preventDefault();
      setErrorMessage('');

      const trimmed = (eventCode || '').trim();

      if (!trimmed) {
        setErrorMessage('Please enter a valid event ID.');
        return;
      }

      try {
        setLoading(true);
        const eventRef = doc(db, 'events', trimmed);
        const eventDoc = await getDoc(eventRef);

        if (eventDoc.exists()) {
          navigate(`/event/${trimmed}`);
        } else {
          setErrorMessage('Event not found.');
        }
      } catch (err) {
        console.error(err);
        setErrorMessage('An error occurred. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [eventCode, navigate]
  );

  return (
    <div className="home-container">
      <main className="home-start-card" role="main">
        <section className="hero-grid">
          <div className="hero-left">
            <span className="home-eyebrow">BINGO • FUNDRAISING • LIVE EVENTS</span>

            <h1 className="welcome-text">
              Run unforgettable events with <span>BuzzBoard.</span>
            </h1>

            <p className="welcome-subtitle">
              The ultimate all-in-one tool for bingo games and fundraising events.
            </p>

            <p className="simply">
              Keep guests connected with live event updates, bingo displays, raffles,
              pull-tabs, and more—all from one simple, professional platform.
            </p>

            <div className="home-feature-list" aria-label="BuzzBoard benefits">
              <div className="home-feature">
                <span className="home-feature-icon" aria-hidden="true">01</span>
                <span>Easy event access</span>
              </div>
              <div className="home-feature">
                <span className="home-feature-icon" aria-hidden="true">02</span>
                <span>Live audience updates</span>
              </div>
              <div className="home-feature">
                <span className="home-feature-icon" aria-hidden="true">03</span>
                <span>Built for fundraisers</span>
              </div>
            </div>

            <div className="event-entry-panel">
              <div className="event-entry-heading">
                <span>JOIN AN EVENT</span>
                <p>Enter the event code shared by your host.</p>
              </div>

              <form
                className="search-container"
                onSubmit={handleSubmit}
                aria-label="Go to event"
              >
                <label htmlFor="eventCode" className="visually-hidden">
                  Event ID
                </label>
                <input
                  id="eventCode"
                  ref={inputRef}
                  type="text"
                  placeholder="Enter Event ID"
                  value={eventCode}
                  onChange={(e) => {
                    setEventCode(e.target.value);
                    setErrorMessage('');
                  }}
                  className="search-input"
                  aria-invalid={!!errorMessage}
                  aria-describedby={errorMessage ? 'error-msg' : undefined}
                />

                <button
                  type="submit"
                  className="search-button"
                  disabled={loading}
                  aria-busy={loading}
                >
                  {loading ? (
                    <span className="spinner" aria-hidden="true" />
                  ) : (
                    <>
                      Go To Event <span aria-hidden="true">→</span>
                    </>
                  )}
                </button>
              </form>

              {errorMessage && (
                <p id="error-msg" className="error-message" role="status">
                  {errorMessage}
                </p>
              )}
            </div>
          </div>

          <aside className="hero-right" aria-label="QR code card">
            <div className="qr-card">
              <div className="qr-card-accent" aria-hidden="true" />
              <span className="qr-label">QUICK ACCESS</span>
              <h2>Scan. Join. Play.</h2>
              <p className="qr-intro">
                Open BuzzBoard instantly on your phone and stay connected to the event.
              </p>

              <div className="qr-frame">
                {qrSrc ? (
                  <img src={qrSrc} alt="Scan to open BuzzBoard" className="qr-image" />
                ) : (
                  <div className="qr-placeholder" aria-hidden="true" />
                )}
              </div>

              <p className="qr-copy">Point your camera at the code to get started.</p>
              <span className="qr-secure-note">No app download required</span>
            </div>
          </aside>
        </section>
      </main>
    </div>
  );
}
